
import React, { useState, useEffect } from "react";
import TeacherLayout from "@/components/TeacherLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DollarSign, TrendingUp, Users, Calendar, CheckCircle, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { createApiUrl } from "@/lib/api";

interface PendingFee {
  fee_id: string;
  amount: number;
  due_date: string;
  status: string;
}

interface PendingFeesStudent {
  student_id: string;
  student_name: string;
  student_phone: string;
  student_email: string;
  class_id: string;
  class_name: string;
  teacher_id: string;
  total_pending: number;
  pending_fees: PendingFee[];
}

interface PendingFeesData {
  user_role: string;
  total_students: number;
  total_pending: number;
  pending_fees: PendingFeesStudent[];
}

const FeesCollection = () => {
  const [pendingFeesData, setPendingFeesData] = useState<PendingFeesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [collectingFees, setCollectingFees] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingFees();
  }, []);

  const fetchPendingFees = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/teacher/login');
        return;
      }

      const response = await fetch(createApiUrl('/teacher/fees/pending'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingFeesData(data);
      } else if (response.status === 401) {
        localStorage.removeItem('teacherData');
        localStorage.removeItem('accessToken');
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        navigate('/teacher/login');
      } else {
        throw new Error('Failed to fetch pending fees');
      }
    } catch (error) {
      console.error('Pending fees fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load pending fees",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const collectFee = async (studentId: string, classId: string, feeAmount: number, dueDate: string) => {
    const feeKey = `${studentId}-${classId}`;
    setCollectingFees(prev => new Set(prev).add(feeKey));

    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/teacher/fees/collect'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: feeAmount,
          due_date: dueDate,
          status: "paid",
          student_id: studentId,
          class_id: classId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Fee Collected",
          description: `₹${data.amount_collected} collected from ${data.student_name}`,
        });
        // Refresh the pending fees data
        fetchPendingFees();
      } else {
        throw new Error('Failed to collect fee');
      }
    } catch (error) {
      console.error('Fee collection error:', error);
      toast({
        title: "Error",
        description: "Failed to collect fee",
        variant: "destructive"
      });
    } finally {
      setCollectingFees(prev => {
        const newSet = new Set(prev);
        newSet.delete(feeKey);
        return newSet;
      });
    }
  };

  if (isLoading) {
    return (
      <TeacherLayout>
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading fees data...</p>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  if (!pendingFeesData) {
    return (
      <TeacherLayout>
        <div className="text-center py-12">
          <p className="text-gray-600 text-sm sm:text-base">Failed to load fees data</p>
        </div>
      </TeacherLayout>
    );
  }

  return (
    <TeacherLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2 flex-wrap">
            <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0" />
            <span>Fees Collection</span>
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">Track and collect student fee payments</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Students</CardTitle>
              <Users className="h-4 w-4 text-blue-600 flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-blue-600">
                {pendingFeesData.total_students}
              </div>
              <p className="text-xs text-muted-foreground">
                Students with pending fees
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Pending</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-orange-600">
                ₹{pendingFeesData.total_pending.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                Amount to be collected
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Collection Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600 flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-green-600">
                {pendingFeesData.total_students > 0 
                  ? Math.round(((pendingFeesData.total_students - pendingFeesData.pending_fees.length) / pendingFeesData.total_students) * 100)
                  : 100}%
              </div>
              <p className="text-xs text-muted-foreground">
                Payment success rate
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Fees */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Pending Fee Collections</CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Students with outstanding fee payments</p>
          </CardHeader>
          <CardContent>
            {pendingFeesData.pending_fees.length > 0 ? (
              <div className="space-y-4">
                {pendingFeesData.pending_fees.map((student) => (
                  <div key={student.student_id} className="border rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row items-start justify-between mb-4 gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-base sm:text-lg break-words">{student.student_name}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{student.class_name}</p>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm text-gray-500">
                          <span className="break-all">{student.student_email}</span>
                          <span className="break-all">{student.student_phone}</span>
                        </div>
                      </div>
                      <div className="text-left sm:text-right">
                        <div className="text-xl sm:text-2xl font-bold text-orange-600">
                          ₹{student.total_pending}
                        </div>
                        <Badge variant="destructive" className="text-xs">
                          {student.pending_fees.length} pending
                        </Badge>
                      </div>
                    </div>

                    {/* Individual Fee Records */}
                    <div className="space-y-2">
                      {student.pending_fees.map((fee) => (
                        <div key={fee.fee_id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-gray-50 rounded-lg gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm sm:text-base">₹{fee.amount}</p>
                            <p className="text-xs sm:text-sm text-gray-600">
                              Due: {new Date(fee.due_date).toLocaleDateString()}
                            </p>
                            <Badge 
                              variant="secondary"
                              className="mt-1 bg-orange-100 text-orange-800 text-xs"
                            >
                              {fee.status}
                            </Badge>
                          </div>
                          <Button
                            onClick={() => collectFee(student.student_id, student.class_id, fee.amount, fee.due_date)}
                            disabled={collectingFees.has(`${student.student_id}-${student.class_id}`)}
                            className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm w-full sm:w-auto"
                            size="sm"
                          >
                            <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                            {collectingFees.has(`${student.student_id}-${student.class_id}`) ? 'Collecting...' : 'Collect'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">All Fees Collected!</h3>
                <p className="text-gray-500 text-sm sm:text-base">
                  Great job! All students have paid their fees.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TeacherLayout>
  );
};

export default FeesCollection;
